import React, { useState } from 'react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUser } from '@/contexts/UserContext';

import { AccountSettings } from '@/components/settings/AccountSettings';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { PrivacySettings } from '@/components/settings/PrivacySettings';
import { SecuritySettings } from '@/components/settings/SecuritySettings';
import { TalentSettings } from '@/components/settings/TalentSettings';
import { AgentSettings } from '@/components/settings/AgentSettings';
import { BrandSettings } from '@/components/settings/BrandSettings';
import { VentureSettings } from '@/components/settings/VentureSettings';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Bell, Shield, Lock, Dumbbell, Briefcase, Building2, TrendingUp, MessageSquarePlus } from 'lucide-react';

const SettingsPage = () => {
  const { accountType } = useUser();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const getAccountSpecificTab = () => {
    switch (accountType) {
      case 'talent':
        return {
          icon: Dumbbell,
          label: 'Talent Settings',
          value: 'talent',
          component: <TalentSettings />
        };
      case 'agent':
        return {
          value: 'agent',
          label: 'Agent Settings',
          icon: Briefcase,
          component: <AgentSettings />
        };
      case 'brand':
        return {
          value: 'brand',
          label: 'Brand Settings',
          icon: Building2,
          component: <BrandSettings />
        };
      case 'venture':
        return {
          value: 'venture',
          label: 'Venture Settings',
          icon: TrendingUp,
          component: <VentureSettings />
        };
      default:
        return null;
    }
  };

  const accountSpecificTab = getAccountSpecificTab();

  return (
    <Layout>
      <PageHeader 
        title="Settings." 
        subtitle="Manage your account preferences and settings"
      />
      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-[1200px]">
        <div className="space-y-6">

          <Tabs defaultValue="account" className="w-full">
            {/* Mobile: horizontal scroll so all 5–6 tabs are reachable (was a 3-col
                grid that silently clipped Security/Feedback/account-specific).
                Desktop (lg+): even 6-column grid. */}
            <TabsList className="flex w-full overflow-x-auto lg:grid lg:grid-cols-6 bg-muted/50 backdrop-blur-sm">
              <TabsTrigger value="account" className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <User className="h-4 w-4" />
                <span>Account</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <Bell className="h-4 w-4" />
                <span>Notifications</span>
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <Shield className="h-4 w-4" />
                <span>Privacy</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <Lock className="h-4 w-4" />
                <span>Security</span>
              </TabsTrigger>
              <TabsTrigger value="feedback" className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <MessageSquarePlus className="h-4 w-4" />
                <span>Feedback</span>
              </TabsTrigger>
              {accountSpecificTab && (
                <TabsTrigger value={accountSpecificTab.value} className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                  <accountSpecificTab.icon className="h-4 w-4" />
                  <span>{accountSpecificTab.label.split(' ')[0]}</span>
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="account" className="space-y-6">
              <AccountSettings />
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6">
              <NotificationSettings />
            </TabsContent>

            <TabsContent value="privacy" className="space-y-6">
              <PrivacySettings />
            </TabsContent>

            <TabsContent value="security" className="space-y-6">
              <SecuritySettings />
            </TabsContent>

            <TabsContent value="feedback" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Share Feedback</CardTitle>
                  <CardDescription>
                    Help us improve TheSlice by sharing your feedback, reporting bugs, or suggesting new features.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setIsFeedbackOpen(true)} className="gap-2">
                    <MessageSquarePlus className="h-4 w-4" />
                    Send Feedback
                  </Button>
                </CardContent>
              </Card>
              <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
            </TabsContent>

            {accountSpecificTab && (
              <TabsContent value={accountSpecificTab.value} className="space-y-6">
                {accountSpecificTab.component}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;